import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
} from '@angular/core';
import { PageEvent } from './page-event';
import { Subject, forkJoin, takeUntil, take } from 'rxjs';
import { ButtonPurpose } from '../button/button-purpose';
import { TranslateService } from '../../services/translate';

@Component({
    selector: 'app-pager',
    templateUrl: './pager.component.html',
    styleUrls: ['./pager.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PagerComponent implements OnInit, OnChanges, OnDestroy {
  readonly startPageCount: number = 1;
  // Changed from readonly to mutable because middlePageCount needs to be updated dynamically
  // based on screen size for responsive pagination button display.
  middlePageCount: number = 5;
  minScreenWidth: number = 320;
  largeScreenWidth: number = 1024;
  minScreenPageCount: number = 2;
  smallScreenPageCount: number = 1;
  readonly endPageCount: number = 1;

  @ContentChild('pagerMessage', { static: true }) pagerMessage: TemplateRef<any>;

  @Input() disabled = false;

  /** The zero-based page index of the displayed list of items. Defaulted to 0. */
  @Input() pageIndex = 0;

  /** The length of the total number of items that are being paginated. Defaulted to 0. */
  @Input() length = 0;

  /** Number of items to display on a page. By default set to 50. */
  @Input() pageSize = 50;

  @Input() ariaLabel = 'Pagination';

  @Output() readonly page: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();

  @HostListener('window:resize')
  onWindowResize() {
    this.updatePageButtonCount();
  }

  /** These are internal-only members.  The getters allow the template access */
  private startPages: number[] = [];
  private middlePages: number[] = [];
  private endPages: number[] = [];

  destroy$ = new Subject<void>();
  _buttonPurpose = ButtonPurpose;
  private boundResizeHandler = this.updatePageButtonCount.bind(this);

  constructor(
    private translateService: TranslateService,
    public liveAnnouncer: LiveAnnouncer,
    private renderer: Renderer2,
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  // Avoids function calls in the template completely by using getters
  get showFirstEllipsis(): boolean {
    return (
      this.startPages.length > 0 && this.startPages[this.startPages.length - 1] + 1 < this.middlePages[0]
    );
  }

  get showLastEllipsis(): boolean {
    return (
      this.endPages.length > 0 && this.middlePages[this.middlePages.length - 1] + 1 < this.endPages[0]
    );
  }

  getStartPages(): number[] {
    return this.startPages;
  }

  getMiddlePages(): number[] {
    return this.middlePages;
  }

  getEndPages(): number[] {
    return this.endPages;
  }

  shouldShowFirstElipsis(): boolean {
    return (
      this.startPages.length > 0 && this.startPages[this.startPages.length - 1] + 1 < this.middlePages[0]
    );
  }

  shouldShowLastElipsis(): boolean {
    return this.endPages.length > 0 && this.middlePages[this.middlePages.length - 1] + 1 < this.endPages[0];
  }

  ngOnInit() {
    this.updatePageButtonCount();
  
    // Fire this initial event so the consuming app knows what items we're viewing
    this.updatePageNumbers();
    this.emitPageEvent(this.pageIndex);
    this.announcePage(this.pageIndex);
  }

  ngOnChanges(): void {
    this.updatePageButtonCount();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  // Dynamically update the number of middle page buttons based on screen width
  updatePageButtonCount(): void {
    const width = window.innerWidth;
    const totalPages = this.getNumberOfPages();
    if (width <= this.minScreenWidth) {
      this.middlePageCount = this.smallScreenPageCount;
    } else if (totalPages > 10 && width > this.minScreenWidth && width < this.largeScreenWidth) {
      this.middlePageCount = this.minScreenPageCount;
    } else {
      this.middlePageCount = 5;
    }
    this.updatePageNumbers();
  }

  private updatePageNumbers(): void {
    const totalPages = this.getNumberOfPages();
    const lastPageIndex = totalPages - 1;
    const maxPageIndexFilter = (i: any) => i <= lastPageIndex;

    if (this.pageIndex > lastPageIndex) {
      this.pageIndex = lastPageIndex;
    }

    // Start pages are always populated first
    this.startPages = Array.from({ length: this.startPageCount }, (v, i) => i).filter(maxPageIndexFilter);

    // If there aren't enough pages to start the middle pages, just set the other arrays to empty
    if (lastPageIndex < this.startPageCount) {
      this.middlePages = [];
      this.endPages = [];
      return;
    }

    // Adjust start index by half the middle page count to put the active page in the middle
    const simpleStartIndex = this.pageIndex - Math.floor(this.middlePageCount / 2);
    const startOfMiddleNumbers = Math.max(
      // Middle numbers must always start after the start numbers
      this.startPageCount,
      // Middle numbers must always end before the end numbers
      Math.min(simpleStartIndex, lastPageIndex - this.middlePageCount)
    );

    this.middlePages = Array.from(
      { length: this.middlePageCount },
      (v, i) => startOfMiddleNumbers + i
    ).filter(maxPageIndexFilter);

    // If there aren't enough pages to fill up the middle section, just set the last section to empty list
    if (lastPageIndex < this.startPageCount + this.middlePageCount) {
      this.endPages = [];
      return;
    }

    this.endPages = Array.from({ length: this.endPageCount }, (v, i) => lastPageIndex - i)
      .filter(maxPageIndexFilter)
      .reverse();
  }

  goToPage(page: number): void {
    if (page === this.pageIndex) {
      return;
    }

    if (this.getNumberOfPages() < page) {
      this.pageIndex = this.getNumberOfPages();
      this.updatePageNumbers();
      return;
    }

    const previousPageIndex: number = this.pageIndex;
    this.pageIndex = page;
    this.updatePageNumbers();
    this.emitPageEvent(previousPageIndex);
    this.announcePage(page);
  }

  announcePage(page: any) {
    const ofText = this.translateService.translate('ngx-shared-of').pipe(take(1));
    // Pager used only for 2-step flow (Content / Settings)
    const pageChanged = this.translateService.translate(`ngx-shared-page-changed-${page + 1}`).pipe(take(1));
    const to = this.translateService.translate('ngx-shared-to').pipe(take(1));
    const showing = this.translateService.translate('ngx-shared-showing').pipe(take(1));

    forkJoin({ ofText, pageChanged, to, showing })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ ofText, pageChanged, to, showing }) => {
        const resultsText = this.getResultsText(this.getPageItems(), ofText, to);
        let announcementText = pageChanged;

        if (resultsText !== '') {
          announcementText = announcementText.concat(` ${showing} ${resultsText}.`);
        }

        this.liveAnnouncer.announce(announcementText);
      });
  }

  nextPage(): void {
    if (!this.hasNextPage()) {
      return;
    }
    this.goToPage(this.pageIndex + 1);
  }

  previousPage(): void {
    if (!this.hasPreviousPage()) {
      return;
    }
    this.goToPage(this.pageIndex - 1);
  }

  /** Whether there is a previous page. */
  hasPreviousPage(): boolean {
    return this.pageIndex >= 1 && this.pageSize !== 0;
  }

  /** Whether there is a next page. */
  hasNextPage(): boolean {
    const maxPageIndex: number = this.getNumberOfPages() - 1;

    return this.pageIndex < maxPageIndex && this.pageSize !== 0;
  }

  /** Calculate the number of pages */
  getNumberOfPages(): number {
    if (!this.pageSize) {
      return 1;
    }

    return Math.max(1, Math.ceil(this.length / this.pageSize));
  }

  nextButtonDisabled(): boolean {
    return this.disabled || !this.hasNextPage();
  }

  previousButtonDisabled(): boolean {
    return this.disabled || !this.hasPreviousPage();
  }

  get firstItemIndex(): number {
    return this.pageIndex * this.pageSize;
  }

  get lastItemIndex(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize - 1, this.length - 1);
  }

  getPageItems() {
    return {
      first: this.firstItemIndex + 1,
      last: this.lastItemIndex + 1,
      count: this.length > 0 ? this.lastItemIndex - this.firstItemIndex + 1 : 0,
      total: this.length,
    };
  }

  getResultsText(pageItems: any, ofTranslation: any, toTranslation: any) {
    if (pageItems.count === 0) {
      return '';
    }

    if (pageItems.count === 1) {
      return `${pageItems.first} ${ofTranslation} ${pageItems.total}`;
    }

    if (pageItems.count > 1) {
      return `${pageItems.first} ${toTranslation} ${pageItems.last} ${ofTranslation} ${pageItems.total}`;
    }

    return '';
  }

  /** Emits an event notifying that a change of the paginator's properties has been triggered. */
  private emitPageEvent(previousPageIndex: number): void {
    this.page.emit({
      previousPageIndex,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      length: this.length,
      firstItemIndex: this.firstItemIndex,
      lastItemIndex: this.lastItemIndex,
    });
  }
}
